import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, Users, FileText, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { OpportunityFunnel } from '@/hooks/useDiversityAnalytics';

interface OpportunityFunnelCardProps {
  data: OpportunityFunnel | undefined;
  isLoading: boolean;
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface FunnelStageProps {
  title: string;
  count: number;
  percentage: number;
  percentageLabel: string;
  icon: React.ElementType;
  color: string;
  isLast?: boolean;
}

function FunnelStage({ 
  title, 
  count, 
  percentage, 
  percentageLabel, 
  icon: Icon, 
  color,
  isLast = false 
}: FunnelStageProps) {
  return (
    <>
      <div className="flex flex-col items-center text-center">
        <div className={cn('rounded-full p-3 mb-3', color)}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <p className="text-3xl font-bold">{count}</p>
        <p className="text-sm font-medium text-muted-foreground mt-1">{title}</p>
        <div className="mt-2 px-3 py-1 rounded-full bg-muted">
          <span className="text-sm font-medium">{formatPercentage(percentage)}</span>
          <span className="text-xs text-muted-foreground ml-1">{percentageLabel}</span>
        </div>
      </div>
      {!isLast && (
        <div className="flex items-center justify-center px-4">
          <ArrowRight className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
    </>
  );
}

export function OpportunityFunnelCard({ data, isLoading }: OpportunityFunnelCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <div className="flex justify-around items-center py-8">
            <Skeleton className="h-32 w-24" />
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-32 w-24" />
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-32 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Opportunity vs Results</CardTitle>
          <CardDescription>
            Track diverse vendor participation through the procurement process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[200px] items-center justify-center text-muted-foreground">
            No participation data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate conversion rates between stages
  const bidToAwardRate = data.jobsWithDiverseBid > 0 
    ? (data.jobsAwardedToDiverse / data.jobsWithDiverseBid) * 100 
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Opportunity vs Results</CardTitle>
        <CardDescription>
          Track diverse vendor participation through the procurement process
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-around items-center py-6">
          <FunnelStage
            title="Jobs with Diverse Bids"
            count={data.jobsWithDiverseBid}
            percentage={data.invitedPercentage}
            percentageLabel="of all jobs"
            icon={FileText}
            color="bg-blue-500"
          />
          <FunnelStage
            title="Jobs Awarded to Diverse"
            count={data.jobsAwardedToDiverse}
            percentage={bidToAwardRate}
            percentageLabel="win rate"
            icon={Award}
            color="bg-emerald-500"
            isLast
          />
        </div>

        {/* Summary stats */}
        <div className="mt-6 pt-6 border-t grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold">{data.totalJobs}</p>
            <p className="text-sm text-muted-foreground">Total Jobs</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{formatPercentage(data.invitedPercentage)}</p>
            <p className="text-sm text-muted-foreground">Jobs with Diverse Participation</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{formatPercentage(bidToAwardRate)}</p>
            <p className="text-sm text-muted-foreground">Diverse Vendor Win Rate</p>
          </div>
        </div>

        {/* Insight callout */}
        {data.jobsWithDiverseBid > 0 && data.jobsAwardedToDiverse === 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Opportunity:</strong> Diverse vendors are bidding but not winning. 
              Review bid evaluation criteria to ensure fair consideration.
            </p>
          </div>
        )}

        {data.invitedPercentage < 30 && data.totalJobs > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Opportunity:</strong> Only {formatPercentage(data.invitedPercentage)} of jobs 
              have diverse vendor participation. Consider expanding vendor outreach.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
